import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import { NTA_RULE_COLUMNS, type NtaRule } from '../lib/ntaRules';

// NTA eligibility thresholds — Admin portal reads and writes, straight through
// Supabase like siteNetworkService. RLS (migrations 155/156) scopes to the org.

const COLUMNS = NTA_RULE_COLUMNS;

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();
  return data?.organisation_id ?? null;
}

const fail = <T>(e: unknown): ApiResponse<T> => ({
  success: false,
  error: e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e),
});

/** PostgREST returns numeric as string; the form and validators want numbers. */
const fromDb = (r: Record<string, unknown>): NtaRule => {
  const n = (v: unknown) => (v == null ? null : Number(v));
  return {
    ...(r as unknown as NtaRule),
    min_demand: n(r.min_demand), max_demand: n(r.max_demand),
    min_usage_kwh: n(r.min_usage_kwh), max_usage_kwh: n(r.max_usage_kwh),
  };
};

const toDb = (r: NtaRule, orgId: string) => {
  const { id: _id, ...rest } = r;
  return { ...rest, org_id: orgId, tariff_code: r.tariff_code.trim().toUpperCase(), distributor: r.distributor.trim() };
};

export const ntaRuleService = {
  list: async (): Promise<ApiResponse<NtaRule[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_nta_eligibility_rule')
        .select(COLUMNS)
        .order('distributor').order('tariff_code').order('effective_from', { ascending: false });
      if (error) throw error;
      return { success: true, data: (data ?? []).map((r) => fromDb(r as unknown as Record<string, unknown>)) };
    } catch (e: unknown) {
      return fail(e);
    }
  },

  save: async (rule: NtaRule): Promise<ApiResponse<NtaRule>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const row = { ...toDb(rule, orgId), loaded_at: new Date().toISOString() };
      const q = rule.id
        ? supabase.from('bv_nta_eligibility_rule').update(row).eq('id', rule.id)
        : supabase.from('bv_nta_eligibility_rule').insert(row);
      const { data, error } = await q.select(COLUMNS).single();
      if (error) throw error;
      return { success: true, data: fromDb(data as unknown as Record<string, unknown>) };
    } catch (e: unknown) {
      return fail(e);
    }
  },

  remove: async (id: string): Promise<ApiResponse<null>> => {
    try {
      const { error } = await supabase.from('bv_nta_eligibility_rule').delete().eq('id', id);
      if (error) throw error;
      return { success: true, data: null };
    } catch (e: unknown) {
      return fail(e);
    }
  },

  /**
   * CSV import: upsert on (org, distributor, tariff_code, effective_from), so
   * re-loading a corrected sheet replaces those rows instead of failing on the
   * unique key. One statement — all or nothing.
   */
  bulkUpsert: async (rules: NtaRule[]): Promise<ApiResponse<number>> => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };
      const now = new Date().toISOString();
      const { error, count } = await supabase
        .from('bv_nta_eligibility_rule')
        .upsert(rules.map((r) => ({ ...toDb(r, orgId), loaded_at: now })), {
          onConflict: 'org_id,distributor,tariff_code,effective_from',
          count: 'exact',
        });
      if (error) throw error;
      return { success: true, data: count ?? rules.length };
    } catch (e: unknown) {
      return fail(e);
    }
  },
};
